import { useMutation, useQueryClient } from "@tanstack/vue-query";
import axios from "axios";
import {
  type BidDetailsProduct,
  type BidPlaced,
  type EventDetails,
  type MaxBid,
  type MaxBidPlaced,
  ProductBidsApi,
  type ProductDetails,
  ProductListingsBidsApi,
} from "mws-ts-v2-sdk";
import { getFormattedCurrencyAmount } from "mws-utils/lib";

import CallToActionToast from "~/components/base/feedback/BaseCallToActionToast.vue";
import { queries } from "~/queries";
import { meQueryKeys } from "~/queries/identity/me";
import { BidAttemptType, BidErrorType } from "~/types/Catalog";
import type { Failure } from "~/utils/errors";

let productBidsApi: ProductBidsApi;
const getProductBidsApi = () => {
  // reduce memory overhead by only using one instance
  if (!productBidsApi) {
    productBidsApi = new ProductBidsApi(
      undefined,
      useRuntimeConfig().public.catalogServiceUrl,
      axios,
    );
  }

  return productBidsApi;
};

let productListingBidsApi: ProductListingsBidsApi;
const getProductListingBidsApi = () => {
  // reduce memory overhead by only using one instance
  if (!productListingBidsApi) {
    productListingBidsApi = new ProductListingsBidsApi(
      undefined,
      useRuntimeConfig().public.catalogServiceUrl,
      axios,
    );
  }

  return productListingBidsApi;
};

type BidMutationRequest = {
  currencyCode: string;
  amount: {
    full: number;
    inCents: number;
  };
  partnerMarketingConsent: boolean;
  customerCountryCode: string;
  bidAttemptType: BidAttemptType;
  paymentTokenId: string | null;
};

export const usePostBidMutation = (
  data: {
    product: MaybeRefOrGetter<ProductDetails | BidDetailsProduct>;
    event?: MaybeRefOrGetter<EventDetails>;
    activeMaxBid?: MaybeRefOrGetter<MaxBid | null>;
  },
  callbacks?: {
    onMaxBidProductBidPlaced?: (data: {
      isOutbid: boolean;
      isExtended: boolean;
    }) => void;
    onNormalProductBidPlaced?: () => void;
    onMaxBidRaised?: () => void;
  },
) => {
  const i18n = useI18n();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { bidPlaced, maxBidPlaced } = useAnalytics();

  const bidError = useBidErrorState();

  const platformStore = usePlatformStore();
  const platformId = computed(
    () => platformStore.getPlatformConfiguration.platformId,
  );
  const { user } = useUser();

  const updatePlatformAfterBid = useUpdatePlatformAfterBid();

  const isMaxBidResponse = (
    bid: MaxBidPlaced | BidPlaced,
  ): bid is MaxBidPlaced => {
    return !("newBid" in bid);
  };

  // BidDetailsProduct doesn't contain this feature flag yet.
  // If we want to add max bid management to my account we should start there.
  const isMaxBidAllowed = (product: ProductDetails | BidDetailsProduct) => {
    return "allowMaxBidding" in product && product.allowMaxBidding;
  };

  const showSuccessBidToast = (
    newProductData: BidPlaced,
    bidRequest: BidMutationRequest,
  ) => {
    if (!bidRequest?.currencyCode) {
      throw new Error(
        "The `onSuccess` for placing a bid is missing original request data.",
      );
    }

    let messageTranslationKey = "product.yourPlacedBid";
    const newEndDate = newProductData.updatedProductEndDate;

    // auction deadline extended
    if (newEndDate > toValue(data.product).endDate) {
      messageTranslationKey = "product.yourPlacedBidExtended";
    }

    const price = getFormattedCurrencyAmount(
      i18n.locale.value,
      bidRequest.currencyCode,
      bidRequest.amount.full * 100,
    );

    const localizedEndDate = Intl.DateTimeFormat(i18n.locale.value, {
      // e.g. "19/12/2020, 22:23:56"
      dateStyle: "short",
      timeStyle: "medium",
    }).format(newEndDate);

    toast.success({
      component: CallToActionToast,
      props: {
        htmlMessage: i18n.t(messageTranslationKey, {
          price,
          newEndDate: localizedEndDate,
        }),
      },
    });
  };

  const handleBidError = async ({
    failure,
    productId,
    bidAmount,
  }: {
    failure: Failure;
    productId: string;
    bidAmount: number | undefined;
  }) => {
    const errorCode = parseInt(failure.errorCode);
    const errorMessage = failure.errorMessage;

    if (errorCode === 400) {
      bidError.value = { message: BidErrorType.BidTooLow };
      toast.error(errorMessage);

      // if the user was able to bid lower than the minimum, it means their product
      // information was not up to date, so we are updating it
      queryClient.invalidateQueries({
        queryKey: queries.products.details(productId, platformId.value)
          .queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: queries.products.details(
          toValue(data.product).slug,
          platformId.value,
        ).queryKey,
      });
      queryClient.invalidateQueries(
        queries.bids.forCurrentUser(`${user.value?.id}`, platformId.value),
      );

      bidError.value = null;
    } else if (errorCode === 403) {
      // to have the most up-to-date user object for correct error handling in `BidLimitWarningModal`
      await queryClient.invalidateQueries(meQueryKeys.detail());

      bidError.value = {
        errorType: BidErrorType.BidLimitExceeded,
        errorCode,
        bidAmount,
        message: errorMessage,
      };
    } else if (errorCode === 404) {
      bidError.value = {
        errorType: BidErrorType.ProductDoesNotExist,
        errorCode,
        errorHandler: () => {
          bidError.value = null;
          setTimeout(window.location.reload.bind(window.location), 1000);
        },
        message: errorMessage,
      };
      toast.error(errorMessage);
    } else if (errorCode === 410) {
      bidError.value = {
        errorType: BidErrorType.BiddingHasClosed,
        errorCode,
        errorHandler: () => {
          bidError.value = null;
          setTimeout(window.location.reload.bind(window.location), 1000);
        },
        message: errorMessage,
      };
      toast.error(errorMessage);
    } else if (errorCode === 424) {
      bidError.value = {
        errorType: BidErrorType.NoBidUserOrNoExchangeRates,
        errorCode,
        message: "custom-i18n-bid-error",
      };
    } else {
      // the page/component can handle this error locally
      // by watching bidError
      return (bidError.value = { message: errorMessage });
    }
  };

  const mutationFn = async (request: BidMutationRequest) => {
    const productId = toValue(data.product).id;

    const {
      currencyCode,
      amount,
      partnerMarketingConsent,
      customerCountryCode,
      bidAttemptType,
      paymentTokenId,
    } = request;

    if (bidAttemptType === BidAttemptType.Maxbid) {
      const response =
        await getProductListingBidsApi().v2ProductListingsProductListingIdBidsMaxPost(
          {
            productListingId: productId,
            v2ProductListingsProductListingIdBidsMaxPostRequest: {
              currencyCode,
              amount: amount.full,
              partnerMarketingConsent,
              customerCountryCode,
              paymentTokenId,
            },
            xPlatformKey: platformId.value,
          },
        );

      return response.data;
    }

    const response = await getProductBidsApi().v2ProductsProductIdBidsPost({
      productId,
      v2ProductsProductIdBidsPostRequest: {
        currencyCode,
        amount: amount.full,
        partnerMarketingConsent,
        customerCountryCode,
        bidType: bidAttemptType,
        tokenId: paymentTokenId,
      },
      xPlatformKey: platformId.value,
    });

    return response.data;
  };

  return useMutation({
    mutationFn,
    onSuccess: (mutationData, variables) => {
      if (
        (!isMaxBidResponse(mutationData) && !mutationData.newBid) ||
        !variables
      ) {
        throw new Error(
          "The `onSuccess` for placing a bid is missing some data",
        );
      }

      const productValue = toValue(data.product);
      const eventValue = toValue(data.event);

      if (isMaxBidResponse(mutationData)) {
        maxBidPlaced(
          productValue,
          eventValue,
          mutationData.maxBid.id,
          variables.amount.full,
          mutationData.isOutbid,
        );
      } else if (mutationData.newBid) {
        bidPlaced(
          productValue,
          eventValue,
          mutationData.newBid.bidId,
          variables.amount.full,
          mutationData.isOutbid,
        );
      }

      updatePlatformAfterBid({
        productId: productValue.id,
        productSlug: productValue.slug,
        eventSlug: eventValue?.slug,
        eventId: eventValue?.id,
        updateMaxBid: isMaxBidResponse(mutationData),
      });

      const formattedPrice = getFormattedCurrencyAmount(
        i18n.locale.value,
        variables.currencyCode,
        variables.amount.inCents,
      );

      if (isMaxBidResponse(mutationData)) {
        if (toValue(data.activeMaxBid)) {
          toast.success(
            i18n.t("product.raiseMaxBidSuccess", {
              PLACEHOLDER_amount: formattedPrice,
            }),
          );

          callbacks?.onMaxBidRaised?.();
        } else {
          callbacks?.onMaxBidProductBidPlaced?.({
            isOutbid: mutationData.isOutbid,
            isExtended: mutationData.isEndDateExtended,
          });
        }
      } else {
        if (isMaxBidAllowed(productValue)) {
          callbacks?.onMaxBidProductBidPlaced?.({
            isOutbid: mutationData.isOutbid,
            isExtended: mutationData.isEndDateExtended,
          });
        } else {
          showSuccessBidToast(mutationData, variables);
          callbacks?.onNormalProductBidPlaced?.();
        }
      }

      if (variables.partnerMarketingConsent) {
        queryClient.invalidateQueries(meQueryKeys.detail());
      }
    },
    onError: async (error: any, variables) => {
      const productId = toValue(data.product).id;
      const statusCodesToHandleExplicitly = [400, 403, 404, 410, 424];
      const statusCodesUsingCustomI18nMessage = [424];

      const errorCode = error.response?.status;
      if (!statusCodesToHandleExplicitly.includes(parseInt(errorCode))) {
        toast.error(error.message);

        return;
      }

      const failure = error.response?.data?.failures?.[0];
      if (failure) {
        await handleBidError({
          failure,
          productId: productId,
          bidAmount: variables.amount.full,
        });

        return;
      }

      // doesn't have failures array, but status code IS to-be handled explicitely
      const usesCustomI18n =
        statusCodesUsingCustomI18nMessage.includes(errorCode);

      return await handleBidError({
        failure: {
          errorCode,
          errorMessage: usesCustomI18n
            ? "custom-i18n-bid-error"
            : error.message,
          propertyName: "",
        },
        bidAmount: variables.amount.full,
        productId: productId,
      });
    },
  });
};
